<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Triage Assessment</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a @click="goTo('queue-management')">Queue</a></li>
                      <li class="breadcrumb-item active">Triage</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3"></div>
              <p class="text-muted mb-0">Loading visit details...</p>
            </div>

            <div v-else class="row">
              <!-- Left: Patient Info -->
              <div class="col-lg-4">
                <!-- Patient Card -->
                <div class="card">
                  <div class="card-body p-4">
                    <div class="d-flex justify-content-between align-items-start mb-3">
                      <h5 class="mb-0"><i class="mdi mdi-account me-2 text-primary"></i>Patient</h5>
                      <span class="queue-badge-sm">{{ visit.queueNo }}</span>
                    </div>

                    <div class="text-center mb-4">
                      <div class="avatar-lg mx-auto mb-3">
                          <span class="avatar-title rounded-circle" :class="visit.gender === 'Male' ? 'bg-primary' : 'bg-pink'" style="font-size: 1.5rem;">
                            {{ getInitials(visit.patientName) }}
                          </span>
                      </div>
                      <h5 class="mb-1">{{ visit.patientName }}</h5>
                      <div class="d-flex justify-content-center flex-wrap gap-1">
                        <span class="badge bg-light text-dark">{{ visit.patientNo }}</span>
                        <span class="badge" :class="visit.gender === 'Male' ? 'bg-primary-subtle text-primary' : 'bg-pink-subtle text-pink'">{{ visit.gender }}</span>
                        <span class="badge bg-light text-dark">{{ visit.age }} years</span>
                      </div>
                    </div>

                    <div class="border-top pt-3">
                      <div class="row text-center">
                        <div class="col-6 border-end">
                          <p class="text-muted mb-1 small">Blood Group</p>
                          <h6 class="mb-0">{{ visit.bloodGroup || 'N/A' }}</h6>
                        </div>
                        <div class="col-6">
                          <p class="text-muted mb-1 small">DOB</p>
                          <h6 class="mb-0">{{ formatDate(visit.dateOfBirth) }}</h6>
                        </div>
                      </div>
                    </div>

                    <!-- Medical Alerts -->
                    <div v-if="visit.allergies" class="alert alert-danger py-2 mt-3 mb-2">
                      <small><i class="mdi mdi-alert-circle me-1"></i><strong>Allergies:</strong> {{ visit.allergies }}</small>
                    </div>
                    <div v-if="visit.chronicConditions" class="alert alert-warning py-2 mb-0">
                      <small><i class="mdi mdi-heart-pulse me-1"></i><strong>Chronic:</strong> {{ visit.chronicConditions }}</small>
                    </div>
                  </div>
                </div>

                <!-- Visit Info Card -->
                <div class="card">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-clipboard-text me-2 text-primary"></i>Visit Details</h6>
                    <table class="table table-sm table-borderless mb-0">
                      <tbody>
                      <tr>
                        <td class="text-muted">Visit No</td>
                        <td class="text-end fw-medium">{{ visit.visitNo }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Type</td>
                        <td class="text-end">
                          <span class="badge" :class="getVisitTypeBadge(visit.visitType)">{{ visit.visitType }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="text-muted">Payment</td>
                        <td class="text-end fw-medium">{{ visit.paymentMode }}</td>
                      </tr>
                      <tr>
                        <td class="text-muted">Check-in</td>
                        <td class="text-end fw-medium">{{ formatDateTime(visit.dateCreated) }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Previous Vitals -->
                <div class="card" v-if="previousVitals.length > 0">
                  <div class="card-body p-4">
                    <h6 class="mb-3"><i class="mdi mdi-history me-2 text-primary"></i>Previous Vitals</h6>
                    <div class="table-responsive">
                      <table class="table table-sm table-bordered mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Date</th>
                          <th>BP</th>
                          <th>Temp</th>
                          <th>Wt</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="vital in previousVitals.slice(0, 3)" :key="vital.vitalID">
                          <td class="small">{{ formatDate(vital.dateCreated) }}</td>
                          <td>{{ vital.bloodPressureSystolic }}/{{ vital.bloodPressureDiastolic }}</td>
                          <td>{{ vital.temperature }}°C</td>
                          <td>{{ vital.weight }}kg</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right: Triage Form -->
              <div class="col-lg-8">
                <div class="card">
                  <div class="card-body p-4">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                      <div>
                        <h5 class="mb-1"><i class="mdi mdi-pulse me-2 text-primary"></i>Triage Assessment</h5>
                        <small class="text-muted">Record vital signs and initial assessment</small>
                      </div>
                    </div>

                    <form @submit.prevent="submitTriage">
                      <!-- Vital Signs Section -->
                      <div class="mb-4">
                        <h6 class="text-uppercase text-muted small mb-3">Vital Signs</h6>
                        <div class="row">
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Temperature <span class="text-danger">*</span></label>
                            <div class="input-group">
                              <input type="number" step="0.1" class="form-control" v-model="triage.temperature"
                                     placeholder="36.5" required min="30" max="45">
                              <span class="input-group-text">°C</span>
                            </div>
                            <small v-if="triage.temperature && (triage.temperature < 36 || triage.temperature > 37.5)" class="text-danger">
                              Abnormal temperature
                            </small>
                          </div>
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Blood Pressure <span class="text-danger">*</span></label>
                            <div class="input-group">
                              <input type="number" class="form-control" v-model="triage.bloodPressureSystolic"
                                     placeholder="120" required min="60" max="250">
                              <span class="input-group-text">/</span>
                              <input type="number" class="form-control" v-model="triage.bloodPressureDiastolic"
                                     placeholder="80" required min="40" max="150">
                              <span class="input-group-text">mmHg</span>
                            </div>
                            <small v-if="bpStatus" :class="bpStatus.class">{{ bpStatus.text }}</small>
                          </div>
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Pulse Rate <span class="text-danger">*</span></label>
                            <div class="input-group">
                              <input type="number" class="form-control" v-model="triage.pulseRate"
                                     placeholder="72" required min="30" max="200">
                              <span class="input-group-text">bpm</span>
                            </div>
                            <small v-if="triage.pulseRate && (triage.pulseRate < 60 || triage.pulseRate > 100)" class="text-warning">
                              Outside normal range (60-100)
                            </small>
                          </div>
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Respiratory Rate <span class="text-danger">*</span></label>
                            <div class="input-group">
                              <input type="number" class="form-control" v-model="triage.respiratoryRate"
                                     placeholder="16" required min="8" max="60">
                              <span class="input-group-text">/min</span>
                            </div>
                          </div>
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Oxygen Saturation <span class="text-danger">*</span></label>
                            <div class="input-group">
                              <input type="number" class="form-control" v-model="triage.oxygenSaturation"
                                     placeholder="98" required min="50" max="100">
                              <span class="input-group-text">%</span>
                            </div>
                            <small v-if="triage.oxygenSaturation && triage.oxygenSaturation < 95" class="text-danger">
                              Low oxygen saturation!
                            </small>
                          </div>
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Pain Score (0-10)</label>
                            <select class="form-select" v-model="triage.painScore">
                              <option value="">Select</option>
                              <option v-for="n in 11" :key="n-1" :value="n-1">{{ n-1 }} - {{ getPainLabel(n-1) }}</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <!-- Measurements Section -->
                      <div class="mb-4">
                        <h6 class="text-uppercase text-muted small mb-3">Measurements</h6>
                        <div class="row">
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Weight <span class="text-danger">*</span></label>
                            <div class="input-group">
                              <input type="number" step="0.1" class="form-control" v-model="triage.weight"
                                     placeholder="70" required min="1" max="500">
                              <span class="input-group-text">kg</span>
                            </div>
                          </div>
                          <div class="col-md-4 mb-3">
                            <label class="form-label">Height <span class="text-danger">*</span></label>
                            <div class="input-group">
                              <input type="number" class="form-control" v-model="triage.height"
                                     placeholder="170" required min="30" max="250">
                              <span class="input-group-text">cm</span>
                            </div>
                          </div>
                          <div class="col-md-4 mb-3">
                            <label class="form-label">BMI (Auto)</label>
                            <div class="input-group">
                              <input type="text" class="form-control bg-light" :value="calculatedBMI" disabled>
                              <span class="input-group-text" :class="bmiClass">{{ bmiCategory }}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Clinical Information -->
                      <div class="mb-4">
                        <h6 class="text-uppercase text-muted small mb-3">Clinical Information</h6>
                        <div class="row">
                          <div class="col-12 mb-3">
                            <label class="form-label">Chief Complaint <span class="text-danger">*</span></label>
                            <textarea class="form-control" rows="2" v-model="triage.chiefComplaint"
                                      :placeholder="visit.chiefComplaint || 'Enter chief complaint...'" required></textarea>
                          </div>
                          <div class="col-12 mb-3">
                            <label class="form-label">History of Present Illness</label>
                            <textarea class="form-control" rows="3" v-model="triage.historyOfPresentIllness"
                                      placeholder="Describe onset, duration, progression, associated symptoms..."></textarea>
                          </div>
                        </div>
                      </div>

                      <!-- Triage Category -->
                      <div class="mb-4">
                        <h6 class="text-uppercase text-muted small mb-3">Triage Category <span class="text-danger">*</span></h6>
                        <div class="row g-2">
                          <div class="col-md-6 col-lg-3">
                            <div class="triage-option" :class="{ 'selected': triage.triageCategory === 'EMERGENCY' }" @click="triage.triageCategory = 'EMERGENCY'">
                              <div class="triage-indicator bg-danger"></div>
                              <div class="triage-content">
                                <h6 class="mb-1">Emergency</h6>
                                <small class="text-muted">Immediate attention</small>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-6 col-lg-3">
                            <div class="triage-option" :class="{ 'selected': triage.triageCategory === 'URGENT' }" @click="triage.triageCategory = 'URGENT'">
                              <div class="triage-indicator bg-warning"></div>
                              <div class="triage-content">
                                <h6 class="mb-1">Urgent</h6>
                                <small class="text-muted">Within 30 min</small>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-6 col-lg-3">
                            <div class="triage-option" :class="{ 'selected': triage.triageCategory === 'LESS_URGENT' }" @click="triage.triageCategory = 'LESS_URGENT'">
                              <div class="triage-indicator bg-info"></div>
                              <div class="triage-content">
                                <h6 class="mb-1">Less Urgent</h6>
                                <small class="text-muted">Can wait 1-2 hours</small>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-6 col-lg-3">
                            <div class="triage-option" :class="{ 'selected': triage.triageCategory === 'NON_URGENT' }" @click="triage.triageCategory = 'NON_URGENT'">
                              <div class="triage-indicator bg-success"></div>
                              <div class="triage-content">
                                <h6 class="mb-1">Non-Urgent</h6>
                                <small class="text-muted">Routine care</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Notes -->
                      <div class="mb-4">
                        <label class="form-label">Triage Notes</label>
                        <textarea class="form-control" rows="2" v-model="triage.triageNotes"
                                  placeholder="Additional observations or notes..."></textarea>
                      </div>

                      <!-- Action Buttons -->
                      <div class="d-flex justify-content-end gap-2 pt-3 border-top">
                        <button type="button" class="btn btn-light" @click="goTo('queue-management')">
                          <i class="mdi mdi-arrow-left me-1"></i> Back to Queue
                        </button>
                        <button type="submit" class="btn btn-primary" :disabled="!canSubmit || submitting">
                            <span v-if="submitting">
                              <i class="mdi mdi-loading mdi-spin me-1"></i> Saving...
                            </span>
                          <span v-else>
                              <i class="mdi mdi-check me-1"></i> Complete Triage
                            </span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital } from "@/api";

export default {
  name: "TriageAssessment",
  components: { NavigationBar, ToastNotification },
  props: {
    visitID: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: true,
      submitting: false,
      currentVisitID: '',
      visit: {},
      previousVitals: [],

      triage: {
        temperature: '',
        bloodPressureSystolic: '',
        bloodPressureDiastolic: '',
        pulseRate: '',
        respiratoryRate: '',
        oxygenSaturation: '',
        weight: '',
        height: '',
        painScore: '',
        chiefComplaint: '',
        historyOfPresentIllness: '',
        triageCategory: '',
        triageNotes: ''
      },

      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: ''
    };
  },
  computed: {
    calculatedBMI() {
      if (this.triage.weight && this.triage.height) {
        const heightM = this.triage.height / 100;
        return (this.triage.weight / (heightM * heightM)).toFixed(1);
      }
      return '-';
    },
    bmiCategory() {
      const bmi = parseFloat(this.calculatedBMI);
      if (isNaN(bmi)) return '-';
      if (bmi < 18.5) return 'Under';
      if (bmi < 25) return 'Normal';
      if (bmi < 30) return 'Over';
      return 'Obese';
    },
    bmiClass() {
      const cat = this.bmiCategory;
      if (cat === 'Normal') return 'bg-success text-white';
      if (cat === 'Under' || cat === 'Over') return 'bg-warning';
      if (cat === 'Obese') return 'bg-danger text-white';
      return '';
    },
    bpStatus() {
      const sys = parseInt(this.triage.bloodPressureSystolic);
      const dia = parseInt(this.triage.bloodPressureDiastolic);
      if (!sys || !dia) return null;

      if (sys < 90 || dia < 60) return { text: 'Low BP (Hypotension)', class: 'text-warning' };
      if (sys <= 120 && dia <= 80) return { text: 'Normal', class: 'text-success' };
      if (sys <= 139 || dia <= 89) return { text: 'Elevated', class: 'text-warning' };
      if (sys >= 180 || dia >= 120) return { text: 'Hypertensive Crisis!', class: 'text-danger fw-bold' };
      return { text: 'High BP', class: 'text-danger' };
    },
    canSubmit() {
      return this.triage.temperature &&
          this.triage.bloodPressureSystolic &&
          this.triage.bloodPressureDiastolic &&
          this.triage.pulseRate &&
          this.triage.respiratoryRate &&
          this.triage.oxygenSaturation &&
          this.triage.weight &&
          this.triage.height &&
          this.triage.chiefComplaint &&
          this.triage.triageCategory;
    }
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatDateTime(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    },

    getVisitTypeBadge(type) {
      const badges = {
        'OPD': 'bg-primary',
        'EMERGENCY': 'bg-danger',
        'REFERRAL': 'bg-info',
        'FOLLOW_UP': 'bg-secondary'
      };
      return badges[type] || 'bg-secondary';
    },

    getPainLabel(score) {
      if (score === 0) return 'No Pain';
      if (score <= 3) return 'Mild';
      if (score <= 6) return 'Moderate';
      if (score <= 9) return 'Severe';
      return 'Worst';
    },

    loadVisit() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getVisit");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.visit = res.data.data.visit;
              this.triage.chiefComplaint = this.visit.chiefComplaint || '';
              this.loadPreviousVitals();
            } else {
              this.$refs.toast.error('Visit not found');
              this.goTo('queue-management');
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error(e);
          });
    },

    loadPreviousVitals() {
      const data = new FormData();
      data.append("function", "getPatientVitals");
      data.append("patientID", this.visit.patientID);
      data.append("limit", "5");

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.previousVitals = res.data.data.vitals || [];
            }
          })
          .catch((e) => console.error(e));
    },

    submitTriage() {
      if (!this.canSubmit) return;

      this.submitting = true;

      const data = new FormData();
      data.append("function", "addTriage");
      data.append("facilityID", this.facilityID);
      data.append("facilityName", this.facilityName);
      data.append("visitID", this.currentVisitID);
      data.append("visitNo", this.visit.visitNo);
      data.append("patientID", this.visit.patientID);
      data.append("patientNo", this.visit.patientNo);

      // Vitals
      data.append("temperature", this.triage.temperature);
      data.append("bloodPressureSystolic", this.triage.bloodPressureSystolic);
      data.append("bloodPressureDiastolic", this.triage.bloodPressureDiastolic);
      data.append("pulseRate", this.triage.pulseRate);
      data.append("respiratoryRate", this.triage.respiratoryRate);
      data.append("oxygenSaturation", this.triage.oxygenSaturation);
      data.append("weight", this.triage.weight);
      data.append("height", this.triage.height);
      data.append("bmi", this.calculatedBMI);
      data.append("painScore", this.triage.painScore);

      // Clinical
      data.append("chiefComplaint", this.triage.chiefComplaint);
      data.append("historyOfPresentIllness", this.triage.historyOfPresentIllness);
      data.append("triageCategory", this.triage.triageCategory);
      data.append("triageNotes", this.triage.triageNotes);
      data.append("triageBy", this.sessionNames);

      // Session
      data.append("sessionNames", this.sessionNames);
      data.append("sessionIdNo", this.sessionIdNo);

      execute(data, hospital)
          .then((res) => {
            this.submitting = false;
            if (res.data.success) {
              this.$refs.toast.success('Triage completed successfully');
              this.$router.push('/queue-management');
            } else {
              this.$refs.toast.error(res.data.message || 'Failed to save triage');
            }
          })
          .catch((e) => {
            this.submitting = false;
            console.error(e);
            this.$refs.toast.error('Error saving triage');
          });
    }
  },
  mounted() {
    this.currentVisitID = this.visitID || this.$route.params.id;
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';

    if (this.currentVisitID) {
      this.loadVisit();
    } else {
      this.$refs.toast.error('No visit specified');
      this.goTo('queue-management');
    }
  }
};
</script>

<style scoped>
/* Avatar */
.avatar-lg { width: 5rem; height: 5rem; }
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
  color: #fff;
}

/* Colors */
.bg-primary-subtle { background-color: rgba(85, 110, 230, 0.15); }
.bg-pink-subtle { background-color: rgba(232, 62, 140, 0.15); }
.bg-pink { background-color: #e83e8c !important; }
.text-pink { color: #e83e8c !important; }

/* Queue Badge Small */
.queue-badge-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #556ee6 0%, #3b5de7 100%);
  color: #fff;
  font-weight: 700;
  border-radius: 8px;
}

/* Triage Options */
.triage-option {
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.triage-option:hover {
  border-color: #556ee6;
}

.triage-option.selected {
  border-color: #556ee6;
  background: rgba(85, 110, 230, 0.05);
}

.triage-indicator {
  width: 12px;
  height: 40px;
  border-radius: 4px;
  margin-right: 12px;
  flex-shrink: 0;
}

.triage-content h6 {
  font-size: 0.9rem;
}

/* Card */
.card {
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}
</style>